import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';

import { Observable, Subscription, filter } from 'rxjs';
import { Site } from 'src/app/model/site';
import { Calendar } from 'src/app/model/calendar';
import * as CalendarActions from 'src/app/store/calendar/calendar.actions';
import { selectCalendarAdded, selectCalendarAdding, selectCalendarEdited, selectCalendarEditing } from 'src/app/store/calendar/calendar.selector';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectUser } from 'src/app/store/user/user.selector';
import { User } from 'src/app/model/user';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	templateUrl: './exceptions-calendar.dialog.component.html',
})

export class ExceptionsCalendarDialogComponent implements OnInit, OnDestroy {
	added$: Observable< boolean | undefined >;
	addedSub: Subscription | undefined;
	adding$: Observable< boolean | undefined >;
	addingSub: Subscription | undefined;
	edited$: Observable< boolean | undefined >;
	editedSub: Subscription | undefined;
	editing$: Observable< boolean | undefined >;
	editingSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	currentUser$: Observable < User | undefined >;
	currentUserSub: Subscription | undefined;

	addCalendarForm: FormGroup;
	add = true;

	added: boolean;
	adding: boolean;
	edited: boolean;
	editing: boolean;

	currentUser: User;
	site: Site;
	calendar?: Calendar;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig
	) {
		if (config.data !== undefined && config.data.calendar !== undefined) {
			this.calendar = config.data.calendar;
			this.adding = false;
		} else {
			this.calendar = new Calendar();
		}
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	buildForm(): void {
		this.addCalendarForm = new FormGroup({
			'name': new FormControl(this.calendar.Name, Validators.required),
			'description': new FormControl(this.calendar.Description, Validators.required),
		});
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.currentUser$ = this.store.select(selectUser);
		this.added$ = this.store.select(selectCalendarAdded);
		this.adding$ = this.store.select(selectCalendarAdding);
		this.edited$ = this.store.select(selectCalendarEdited);
		this.editing$ = this.store.select(selectCalendarEditing);
	}

	subscribeAll(): void {
		this.addedSub = this.added$?.pipe(filter((added) => !!added))
			.subscribe((added) => {
				this.resetClose();
				this.added = added;
			});
		this.addingSub = this.adding$?.subscribe((adding) => {
			this.adding = adding;
		});
		this.editedSub = this.edited$?.pipe(filter((edit) => !!edit))
			.subscribe((edited) => {
				this.resetClose();
				this.edited = edited;
			});
		this.editingSub = this.editing$?.subscribe((editing) => {
			this.editing = editing;
		});
		this.siteSub = this.site$?.subscribe(site => {
			this.site = site;
		});
		this.currentUserSub = this.currentUser$.subscribe((currentUser) => {
			this.currentUser = currentUser;
		});
	}

	ngOnDestroy(): void {
		this.siteSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.addedSub?.unsubscribe();
		this.addingSub?.unsubscribe();
		this.editedSub?.unsubscribe();
		this.editingSub?.unsubscribe();
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.addCalendarForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.addCalendarForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const name = this.f.name.value;
		const description = this.f.description.value;

		this.calendar = {
			...this.calendar,
			Name: name,
			Description: description,
			SiteId: this.site.Id,
			UserId: this.currentUser.Id
		};
		if (this.calendar.Id) {
			this.store.dispatch(CalendarActions.editCalendar({ calendar: this.calendar }));
		} else {
			this.store.dispatch(CalendarActions.addCalendar({ calendar: this.calendar }));
		}
	}

	get f(): any {
		return this.addCalendarForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	resetClose(): void {
		this.store.dispatch(CalendarActions.resetCalendarDialog());
		this.closeDialog();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth,'w-20rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	showErrorConfirmInvalidMessage(type: ExceptionsCalendarDialogComponent['f'], notEquivalentType: string): boolean {
		return StandardNewValidator.showErrorConfirmInvalidMessage(type,notEquivalentType,this.addCalendarForm);
	}

	newShowErrors(type: ExceptionsCalendarDialogComponent["f"], classType: string) : string {
		return StandardNewValidator.newShowErrors(type,classType,this.screenWidth);
	}

	showErrorRequiredMessage(type: ExceptionsCalendarDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}
}
